/* BrandOS: payment preference is confirmed in the cart, never before add-to-cart. */
(() => {
  'use strict';
  if (window.__brandosCartPaymentV5) return;
  window.__brandosCartPaymentV5 = true;
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const boxes = () => [...document.querySelectorAll('[data-brandos-payment-cart]')];
  const validMode = value => value === 'prepaid' || value === 'cod';
  const modeFromCart = cart => {
    const value = cart.attributes?.brandos_payment_preference;
    return value === 'PREPAID' ? 'prepaid' : value === 'COD' ? 'cod' : '';
  };
  const codesFromCart = cart => (cart.discount_codes || []).map(entry => entry.code).filter(Boolean);
  let desiredMode = '';
  let runner = null;
  let checkoutBusy = false;
  let displayVersion = 0;
  let refreshTimer;
  const allowedSubmits = new WeakSet();

  const prompt = (message, error = false) => boxes().forEach(box => {
    const node = box.querySelector('[data-brandos-choice-error]');
    if (!node) return;
    node.textContent = message;
    node.dataset.error = String(error);
  });
  const paint = mode => boxes().forEach(box => {
    box.dataset.brandosPaymentMode = mode;
    box.querySelectorAll('[data-brandos-cart-choice]').forEach(input => { input.checked = input.value === mode; });
  });
  const money = (amount, currency = 'INR') => new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount / 100);
  const renderTotals = cart => {
    // A discount can change line-item keys; keep native quantity/remove controls current.
    (cart.items || []).forEach((item, index) => {
      document.querySelectorAll(`[data-index="${index + 1}"], [data-quantity-variant-id="${item.variant_id}"]`).forEach(node => {
        if (node.hasAttribute('data-key')) node.setAttribute('data-key', item.key);
      });
    });
    const itemsSubtotal = cart.items_subtotal_price ?? (cart.items || []).reduce((sum, item) => sum + item.final_line_price, 0);
    const orderDiscount = (cart.cart_level_discount_applications || []).reduce((sum, entry) => sum + (entry.amount ?? (entry.discount_application || entry).total_allocated_amount ?? 0), 0);
    const subtotal = Math.max(0, itemsSubtotal - orderDiscount);
    const mode = modeFromCart(cart) || currentChoice();
    // The real COD fee remains a checkout shipping charge, never a cart item.
    const codFee = mode === 'cod' && cart.item_count > 0 && (!cart.currency || cart.currency === 'INR') ? 5000 : 0;
    document.querySelectorAll('[data-brandos-cart-total], cart-drawer .cart_total span:last-child').forEach(node => {
      node.textContent = money(subtotal + codFee, cart.currency);
      const row = node.closest('.cart_total, .brandos-full-cart__total');
      if (!row) return;
      const label = row.querySelector('span:first-child, strong:first-child');
      if (label && label !== node) {
        label.dataset.brandosOriginalLabel ||= label.textContent;
        label.textContent = codFee ? 'Estimated total' : label.dataset.brandosOriginalLabel;
      }
      let summary = row.previousElementSibling;
      if (!summary?.hasAttribute('data-brandos-cod-summary')) {
        summary = document.createElement('div');
        summary.setAttribute('data-brandos-cod-summary', '');
        summary.style.cssText = 'font-size:12px;line-height:1.4;margin:0 0 6px';
        row.before(summary);
      }
      summary.replaceChildren();
      summary.hidden = !codFee;
      if (codFee) {
        for (const [title, value] of [
          ['Subtotal after discounts', money(subtotal, cart.currency)],
          ['COD fee', '+' + money(codFee, cart.currency)]
        ]) {
          const line = document.createElement('div');
          line.style.cssText = 'display:flex;justify-content:space-between;gap:8px;margin:0 0 3px';
          const name = document.createElement('span');
          const amount = document.createElement('span');
          name.textContent = title;
          amount.textContent = value;
          line.append(name, amount);
          summary.append(line);
        }
      }
    });
    document.querySelectorAll('[data-brandos-cart-discounts]').forEach(list => {
      list.replaceChildren();
      (cart.cart_level_discount_applications || []).forEach(entry => {
        const discount = entry.discount_application || entry;
        const li = document.createElement('li');
        li.textContent = `${discount.title}: -${money(entry.amount ?? discount.total_allocated_amount, cart.currency)}`;
        list.append(li);
      });
    });
    const drawerList = document.querySelector('cart-drawer .cart_btns .discounts');
    if (drawerList) {
      drawerList.replaceChildren();
      (cart.cart_level_discount_applications || []).forEach(entry => {
        const discount = entry.discount_application || entry;
        const li = document.createElement('li');
        li.className = 'discounts__discount discounts__discount--end';
        li.textContent = `${discount.title} (-${money(entry.amount ?? discount.total_allocated_amount, cart.currency)})`;
        drawerList.append(li);
      });
    }
  };
  const fetchJson = async (path, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(rootPath() + path, { ...options, signal: controller.signal });
      if (!response.ok) throw new Error('cart_request_failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  };
  const fetchCart = () => fetchJson('cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
  const saveMode = async mode => {
    if (!validMode(mode)) throw new Error('invalid_payment_preference');
    const before = await fetchCart();
    const codes = codesFromCart(before);
    const otherCodes = codes.filter(code => code.toUpperCase() !== 'PREPAID5');
    // Keep an existing campaign/customer coupon. Never replace it with PREPAID5.
    const nextCodes = mode === 'prepaid' && !otherCodes.length ? ['PREPAID5'] : otherCodes;
    const cart = await fetchJson('cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          brandos_payment_preference: mode === 'prepaid' ? 'PREPAID' : 'COD',
          brandos_prepaid_offer: mode === 'prepaid' && nextCodes.some(code => code.toUpperCase() === 'PREPAID5') ? 'PREPAID5' : ''
        },
        discount: nextCodes.join(',')
      })
    });
    if (modeFromCart(cart) !== mode) throw new Error('preference_not_saved');
    return cart;
  };
  const runSync = async () => {
    let cart;
    do {
      const target = desiredMode;
      cart = await saveMode(target);
      if (target !== desiredMode) continue;
      paint(target);
      renderTotals(cart);
      const prepaidApplied = (cart.discount_codes || []).some(code => code.code.toUpperCase() === 'PREPAID5' && code.applicable);
      const message = target === 'cod'
        ? 'COD selected. The estimate includes the ₹50 COD fee. Final shipping is confirmed at checkout.'
        : prepaidApplied
          ? 'PREPAID5 applied. Choose UPI or card at checkout.'
          : 'Your existing coupon is kept. Order discounts do not stack.';
      prompt(message);
      return cart;
    } while (true);
  };
  const sync = mode => {
    displayVersion += 1;
    desiredMode = mode;
    if (!runner) runner = runSync().finally(() => { runner = null; });
    return runner;
  };
  const currentChoice = () => {
    const input = document.querySelector('[data-brandos-cart-choice]:checked');
    return validMode(input?.value) ? input.value : 'prepaid';
  };

  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof Element) || !input.matches('[data-brandos-cart-choice]')) return;
    if (checkoutBusy) return;
    paint(input.value);
    prompt('Saving your payment preference...');
    sync(input.value).catch(() => prompt('Unable to save your preference. Check your connection and choose again before checkout.', true));
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
    if (allowedSubmits.has(form)) { allowedSubmits.delete(form); return; }
    if (!boxes().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    const mode = currentChoice();
    // Use the visible default; do not add an empty-choice gate before checkout.
    paint(mode);
    checkoutBusy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    boxes().forEach(box => box.querySelectorAll('input').forEach(input => { input.disabled = true; }));
    try {
      prompt('Confirming your payment preference...');
      await sync(mode);
      // Wait for cart attributes and discount to persist before the native checkout POST.
      const verified = await fetchCart();
      if (modeFromCart(verified) !== mode) throw new Error('preference_changed');
      if (mode === 'cod' && (verified.discount_codes || []).some(code => code.code.toUpperCase() === 'PREPAID5' && code.applicable)) throw new Error('prepaid_discount_on_cod');
      allowedSubmits.add(form);
      button.disabled = false;
      form.requestSubmit(button);
    } catch (error) {
      prompt('Unable to confirm your payment preference. Your cart is saved. Please try again.', true);
    } finally {
      checkoutBusy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      boxes().forEach(box => box.querySelectorAll('input').forEach(input => { input.disabled = false; }));
    }
  }, true);
  // Reapply the quote after native cart sections refresh, without changing the cart.
  const refreshDisplay = () => {
    const version = ++displayVersion;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      if (runner || checkoutBusy || !boxes().length) return;
      try {
        const cart = await fetchCart();
        if (version !== displayVersion || runner || checkoutBusy) return;
        paint(modeFromCart(cart) || currentChoice());
        renderTotals(cart);
      } catch (_) {
        // A failed read must not change the last confirmed cart or payment choice.
      }
    }, 80);
  };
  const startDisplay = () => {
    refreshDisplay();
    document.addEventListener('brandos:coupon-applied', refreshDisplay);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshDisplay);
    }
    new MutationObserver(records => {
      const selector = '[data-brandos-payment-cart], [data-brandos-cart-total], .cart_total, .cart-item';
      if (records.some(record => [...record.addedNodes].some(node =>
        node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
      ))) refreshDisplay();
    }).observe(document.documentElement, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startDisplay, { once: true });
  else startDisplay();
  // No cart write, discount change, localStorage reset, or add-to-cart interception on page load.
})();
